// PortCartTransport — cart-service transport backed by the CommercePort.
//
// The migration recorded on coord ASK-055: CartService keeps its UI-facing
// surface (lines / onChange / addItem / checkout); the BACKEND goes behind the
// port (DECISION-035v2 §1 — head reads port-typed objects only). This transport
// is the bridge: it satisfies the existing cart-service transport contract
// (loadInitial / addItem / removeItem / clear / hydrate / checkout) by calling
// a CommercePort instance (the Medusa adapter for Kapow/M6).
//
// Ships READY but UNSELECTED: app.js keeps choosing Local/Gcg until the Medusa
// Store API URL + publishable key land (ASK-055) — then selection is a config
// flip, not a build. One cart abstraction, not two.
//
// AAA precedent: Unreal Online Subsystem — the head's cart UI is backend-blind;
// this transport is the seam where the OSS-style port slots in.

const CART_ID_KEY_PREFIX = "kapowx_port_cart.";   // per-shop cart id persistence

function formatMoney(money) {
	if (!money || typeof money.amount !== "number") return "";
	const units = (money.amount / 100).toFixed(2);
	const cur = money.currencyCode === "ZAR" ? "R" : `${money.currencyCode} `;
	return `${cur}${units}`;
}

// PortCartLine → the UI line shape the cart panel renders ({id, title, price, quantity}).
function toUiLine(l) {
	return { id: l.lineId, title: l.title, price: formatMoney(l.unitPrice), quantity: l.quantity, sku: l.sku, variantId: l.variantId };
}

export class PortCartTransport {
	supportsCheckout = true;

	/**
	 * @param {object} cfg
	 * @param {import('./port').CommercePort} cfg.port  the adapter (MedusaStoreAdapter)
	 * @param {string} cfg.shopId
	 */
	constructor({ port, shopId, customerEmail = null, devFallbackVariant = false }) {
		this.port = port;
		this.shopId = shopId;
		this.customerEmail = customerEmail;
		// Dev-test mode only: when a GG product id has no Medusa counterpart
		// (catalogue not yet authored — chunk 7), fall back to the first
		// available Medusa product so the money loop is testable end-to-end.
		// The cart line shows the REAL Medusa title, so the substitution is
		// visible, never silent. Off outside the ?medusaShop=1 flag.
		this.devFallbackVariant = devFallbackVariant;
		this._cartId = this._loadCartId();
		this._variantCache = new Map();   // product handle/sku → variantId
	}

	_cartKey() { return `${CART_ID_KEY_PREFIX}${this.shopId}`; }
	_loadCartId() {
		try { return localStorage.getItem(this._cartKey()) || null; } catch { return null; }
	}
	_saveCartId(id) {
		this._cartId = id;
		try { localStorage.setItem(this._cartKey(), id); } catch { /* memory-only */ }
	}
	_clearCartId() {
		this._cartId = null;
		try { localStorage.removeItem(this._cartKey()); } catch { /* noop */ }
	}

	loadInitial() {
		// Backend is the source of truth — contents arrive via hydrate().
		return [];
	}

	async _ensureCart() {
		if (this._cartId) return this._cartId;
		const cart = await this.port.cart.createCart(this.shopId);
		this._saveCartId(cart.cartId);
		return cart.cartId;
	}

	// Runtime products carry GG ids (e.g. "batman-706"), not backend variant ids.
	// Resolve through the catalog port (handle/sku lookup) + cache. The Medusa
	// seed maps GG SKUs onto product handles, so this resolves 1:1 for Kapow.
	async _resolveVariantId(product) {
		const key = String(product.id || product.sku);
		if (this._variantCache.has(key)) return this._variantCache.get(key);
		let portProduct = await this.port.catalog.getProduct(key);
		let variant = portProduct?.variants?.[0];
		if (!variant && this.devFallbackVariant) {
			const { items } = await this.port.catalog.listProducts({ limit: 1 });
			variant = items[0]?.variants?.[0];
			if (variant) console.info(`[port-cart] dev fallback: '${key}' not in Medusa yet → adding '${items[0].title}'`);
		}
		if (!variant) throw new Error(`no variant for product '${key}'`);
		this._variantCache.set(key, variant.variantId);
		return variant.variantId;
	}

	async hydrate() {
		if (!this._cartId) return { ok: true, lines: [] };
		try {
			const cart = await this.port.cart.getCart(this._cartId);
			if (!cart) { this._clearCartId(); return { ok: true, lines: [] }; }
			return { ok: true, lines: cart.lines.map(toUiLine) };
		} catch (err) {
			console.warn("[port-cart] hydrate failed:", err?.message || err);
			return { ok: false, lines: [] };
		}
	}

	async addItem(product) {
		try {
			const cartId = await this._ensureCart();
			const variantId = await this._resolveVariantId(product);
			try {
				const cart = await this.port.cart.addLine(cartId, variantId, 1);
				return { ok: true, lines: cart.lines.map(toUiLine) };
			} catch (err) {
				// Stale stored cart (already paid/completed, or expired): start a
				// fresh one and retry once. Covers the interrupted-checkout case
				// where the previous cart already carries an authorized payment.
				if (err?.status && err.status >= 400 && err.status < 500) {
					console.info("[port-cart] stored cart stale — starting fresh:", err.message);
					this._clearCartId();
					const freshId = await this._ensureCart();
					const cart = await this.port.cart.addLine(freshId, variantId, 1);
					return { ok: true, lines: cart.lines.map(toUiLine) };
				}
				throw err;
			}
		} catch (err) {
			console.error("[port-cart] addItem failed:", err?.message || err);
			return { ok: false, error: "add_failed" };
		}
	}

	async removeItem(lineId) {
		try {
			const cart = await this.port.cart.removeLine(this._cartId, lineId);
			return { ok: true, lines: cart.lines.map(toUiLine) };
		} catch (err) {
			console.error("[port-cart] removeItem failed:", err?.message || err);
			return { ok: false, error: "remove_failed" };
		}
	}

	async clear() {
		try {
			if (!this._cartId) return { ok: true, lines: [] };
			let cart = await this.port.cart.getCart(this._cartId);
			for (const l of (cart?.lines || [])) {
				cart = await this.port.cart.removeLine(this._cartId, l.lineId);
			}
			return { ok: true, lines: [] };
		} catch (err) {
			console.error("[port-cart] clear failed:", err?.message || err);
			return { ok: false, error: "clear_failed" };
		}
	}

	// Stale-pricing revalidation at checkout entry (DECISION-031 §3.1 A3):
	// re-fetch the cart from the backend so price + stock are current BEFORE the
	// customer reaches payment. Returns the fresh lines; the caller compares
	// against the local mirror + surfaces any drift to the customer.
	async revalidate() {
		if (!this._cartId) return { ok: true, lines: [] };
		try {
			const cart = await this.port.cart.getCart(this._cartId);
			return { ok: true, lines: (cart?.lines || []).map(toUiLine), subtotal: formatMoney(cart?.subtotal) };
		} catch (err) {
			return { ok: false, error: "revalidate_failed" };
		}
	}

	// Post-redirect completion (Paystack sends the customer back with
	// ?trxref=&reference=). Completes the stored cart into an order; the
	// Paystack webhook authorizes the payment server-side, so a still-pending
	// completion is retried once. Clears the stored cart id on success.
	async completeAfterReturn() {
		if (!this._cartId) return { ok: false, error: "no_pending_cart" };
		const attempt = () => this.port.checkout.completeCheckout(this._cartId);
		try {
			let order;
			try { order = await attempt(); }
			catch (first) {
				// Webhook race: wait + retry once before reporting pending.
				await new Promise((r) => setTimeout(r, 2500));
				order = await attempt();
			}
			this._clearCartId();
			return { ok: true, order };
		} catch (err) {
			console.warn("[port-cart] completion pending/failed:", err?.message || err);
			return { ok: false, error: "completion_pending", detail: err?.message };
		}
	}

	// Redirect-mode checkout: pick the region's provider (Paystack for ZA) +
	// create a checkout session; the head navigates to redirectUrl. Embedded
	// mode arrives when the live provider list confirms clientSecret support.
	async checkout() {
		try {
			// Revalidate before payment — DECISION-031 §3.1 A3 gate.
			const fresh = await this.revalidate();
			if (!fresh.ok) return { ok: false, error: "revalidate_failed" };

			const providers = await this.port.checkout.getPaymentProviders(this.shopId, "ZA");
			const provider = providers.find((p) => p.providerId === "paystack") || providers[0];
			if (!provider) return { ok: false, error: "no_payment_provider" };

			const session = await this.port.checkout.createCheckoutSession(this._cartId, provider.providerId, { email: this.customerEmail });
			if (session.mode === "redirect" && session.redirectUrl) {
				return { ok: true, url: session.redirectUrl, sellerOfRecord: session.sellerOfRecord };
			}
			return { ok: true, kind: "embedded", session };
		} catch (err) {
			console.error("[port-cart] checkout failed:", err?.message || err);
			return { ok: false, error: "checkout_failed" };
		}
	}
}
